// src/api/client.ts

import axios, { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { ApiResponse } from '../types';

/**
 * Класс для работы с API
 * Здесь мы настраиваем базовый URL и перехватчики запросов
 */
class ApiClient {
    private client: AxiosInstance;

    constructor() {
        this.client = axios.create({
            baseURL: '/api',
            timeout: 10000,
            headers: {
                'Content-Type': 'application/json',
            },
            // КРИТИЧЕСКИ ВАЖНО: передаём cookies (JSESSIONID) с каждым запросом
            withCredentials: true,
        });

        // Перехватчик запросов — добавляем CSRF-токен для небезопасных методов
        this.client.interceptors.request.use(
            (config: InternalAxiosRequestConfig) => {
                const method = (config.method || 'get').toLowerCase();
                const unsafeMethods = ['post', 'put', 'delete', 'patch'];

                if (unsafeMethods.includes(method)) {
                    // Читаем CSRF-токен из cookie
                    const csrfToken = this.getCookie('XSRF-TOKEN');
                    if (csrfToken) {
                        config.headers['X-XSRF-TOKEN'] = csrfToken;
                    }
                }

                return config;
            },
            (error) => Promise.reject(error)
        );

        // Перехватчик ответов — обрабатываем ошибки
        this.client.interceptors.response.use(
            (response) => response,
            (error) => {
                // 401 — пользователь не авторизован
                if (error.response?.status === 401) {
                    // Не редиректим автоматически здесь — этим занимается AuthContext
                    console.warn('Пользователь не авторизован');
                }
                return Promise.reject(error);
            }
        );
    }

    /**
     * Читает cookie по имени
     */
    private getCookie(name: string): string | null {
        const match = document.cookie.match(new RegExp('(^|;\\s*)' + name + '=([^;]*)'));
        return match ? decodeURIComponent(match[2]) : null;
    }

    async get<T>(url: string): Promise<ApiResponse<T>> {
        const response: AxiosResponse<ApiResponse<T>> = await this.client.get(url);
        return response.data;
    }

    async post<T>(url: string, data?: any, config?: any): Promise<ApiResponse<T>> {
        const response: AxiosResponse<ApiResponse<T>> = await this.client.post(url, data, config);
        return response.data;
    }

    async put<T>(url: string, data?: any): Promise<ApiResponse<T>> {
        const response: AxiosResponse<ApiResponse<T>> = await this.client.put(url, data);
        return response.data;
    }

    async delete<T>(url: string): Promise<ApiResponse<T>> {
        const response: AxiosResponse<ApiResponse<T>> = await this.client.delete(url);
        return response.data;
    }

    /**
     * Специальный метод для логина (form-data)
     * Spring Security ожидает application/x-www-form-urlencoded
     */
    async postForm<T>(url: string, data: Record<string, string>): Promise<T> {
        const formData = new URLSearchParams();
        Object.entries(data).forEach(([key, value]) => {
            formData.append(key, value);
        });

        const response = await this.client.post<T>(url, formData, {
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
        });
        return response.data;
    }
}

export const apiClient = new ApiClient();